import { Button, TextField, Typography } from 'darrius';
import { useContext, useEffect, useState } from 'react';

import { showToast } from '../../../atoms/Toast/Toast';

import { CheckoutContext, IPaymentCard } from '../../../../context/Checkout';
import { axiosInstance } from '../../../../utils/axiosInstance';
import {
  addCreditCardCvvMask,
  addCreditCardNumberMask,
  addCreditCardValidateDateMask,
  onlyNumbers,
} from '../../../../utils/parsedMasks';
import { creditCardPatterns } from '../../../../utils/fieldPatterns';

import {
  type ICreditCard,
  type TFildsCreditCard,
} from '../../../../types/CreditCard';
import { RecurrencyModal } from '../../../atoms';
import { customTracking } from '../../../../utils/customTracking';

import styles from './CreditCardView.module.scss';

export const CreditCardView = ({
  appButton,
  origin,
  paymentCards,
  isMercadoPago,
  userState,
  isIuguActive,
  userInfo,
}) => {
  const [showCoupon, setShowCoupon] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [fieldErrorMessage, setFieldErrorMessage] = useState<any>({});
  const [showCompletedPurchase, setShowCompletedPurchase] = useState(false);
  const [creditCardData, setCreditCardData] = useState<ICreditCard>({
    name: '',
    cvv: '',
    number: '',
    renew: true,
    validDate: '',
    numberInstallments: 1,
  });
  const [selectedCard, setSelectedCard] = useState<IPaymentCard | null>(null);

  const { checkoutData, updateCheckoutData } = useContext(CheckoutContext);

  const activeBundle = checkoutData.bundlesData.find(
    (bundle) => bundle.nameId === checkoutData.currentBundleName
  );

  function handleChangeValueForm(value: string, field: TFildsCreditCard) {
    const errorMessage = validateFields(value, field);

    if (errorMessage) {
      setFieldErrorMessage({ [field]: errorMessage });
    } else {
      setFieldErrorMessage({});
    }

    setCreditCardData((oldState) => ({
      ...oldState,
      [field]: value,
      cvv: field === 'cvv' ? addCreditCardCvvMask(value) : oldState.cvv,
      number:
        field === 'number' ? addCreditCardNumberMask(value) : oldState.number,
      validDate:
        field === 'validDate'
          ? addCreditCardValidateDateMask(value)
          : oldState.validDate,
    }));
  }

  const parseCreditCardData = () => {
    return {
      auth_token: checkoutData.authToken,
      bundle: activeBundle.purchaseLink,
      coupon: checkoutData.coupon || '',
      number_of_installments: creditCardData.numberInstallments,
      origin: origin,
      partner_type: selectedCard?.partner_type,
      payment_method: 'credit',
      recurrency: creditCardData.renew,
      remember: false,
      token: selectedCard?.token || '',
      token_v5: selectedCard?.token || '',
      user_state: userState,
    };
  };

  const parseMpCreditCardData = () => {
    if (selectedCard) {
      return {
        authToken: checkoutData.authToken,
        activeBundleId: activeBundle.id,
        description: activeBundle.purchaseLink,
        price: activeBundle.totalPrice,
        cvv: creditCardData.cvv,
        cardId: selectedCard.token,
        brand: selectedCard.brand,
        installmentsNumber: creditCardData.numberInstallments,
        customerId: selectedCard.customer_id,
      };
    }

    return null;
  };

  const parseErrorMessage = (error) => {
    if (!error?.response?.config?.data) return null;
    let errorPayload = JSON.parse(error?.response?.config?.data);
    if (errorPayload?.creditCardData) {
      errorPayload.creditCardData = null;
    }

    return errorPayload;
  };

  const handleErrorsMercadoPago = (error, payload) => {
    if (window?.newrelic) {
      window.newrelic.noticeError(error, {
        step: 'creditcard_payment_mercado_pago_error',
        bundle_id: activeBundle?.id,
        origin: origin,
        status: error?.response?.status,
        statusMessage: error?.response?.data?.status,
        message: error?.response?.data?.clientMessage || error?.message,
        type: 'new_card_mercado_pago',
        request_info: payload,
      });

      window.newrelic.addPageAction('webcheckout_action', {
        step: 'creditcard_payment_mercado_pago_error',
        bundle_id: activeBundle?.id,
        origin: origin,
        status: error?.response?.status,
        statusMessage: error?.response?.data?.status,
        message: error?.response?.data?.clientMessage || error?.message,
        type: 'new_card_mercado_pago',
        request_info: payload,
      });
    }
  };

  let count = 0;

  async function handleBuyWithCreditCard() {
    setIsLoading(true);
    let requestInfoData;

    try {
      const requestOptions = {
        method: 'POST',
        url: `${process.env.NEXT_PUBLIC_API_PRO}/purchase/credit_card`,
        headers: {
          'Content-Type': 'application/json',
        },
        data: {
          authToken: checkoutData.authToken,
          creditCardData,
          generateTokenInfos: activeBundle.generateTokenInfos,
          purchaseLink: activeBundle.purchaseLink,
          coupon: checkoutData.coupon || '',
          origin,
          userState,
          isIuguActive,
        },
      };

      const requestPurchaseCreditCardInfo = {
        method: 'POST',
        url: `${process.env.NEXT_PUBLIC_API_PRO}/purchase/saved_credit_card`,
        headers: {
          'Content-Type': 'application/json',
        },
        data: parseCreditCardData(),
      };

      const requestPurchaseCreditCardMercadoPago = {
        method: 'POST',
        url: `${process.env.NEXT_PUBLIC_API_PRO}/purchase/credit_card_mercado_pago`,
        headers: {
          'Content-Type': 'application/json',
        },
        data: {
          authToken: checkoutData.authToken,
          creditCardData,
          purchaseLink: activeBundle.purchaseLink,
          activeBundle,
        },
      };

      const requestPurchaseSavedCreditCardMP = {
        method: 'POST',
        url: `${process.env.NEXT_PUBLIC_API_PRO}/purchase/saved_credit_card_mp`,
        headers: {
          'Content-Type': 'application/json',
        },
        data: parseMpCreditCardData(),
      };

      let requestInfo;

      requestInfo = selectedCard
        ? requestPurchaseCreditCardInfo
        : requestOptions;

      if (isMercadoPago && count === 0) {
        requestInfo = selectedCard
          ? requestPurchaseSavedCreditCardMP
          : requestPurchaseCreditCardMercadoPago;
      }

      if (selectedCard) {
        requestInfoData = requestInfo.data;
      }

      const { data } = await axiosInstance(requestInfo);

      if (selectedCard && isMercadoPago) {
        handleChangeValueForm('', 'cvv');
        setFieldErrorMessage({});
      }

      showToast({
        message: 'Pagamento realizado com sucesso',
        type: 'success',
      });

      setShowCompletedPurchase(true);

      if (window?.newrelic) {
        window.newrelic.addPageAction('webcheckout_action', {
          step: 'creditcard_payment_success',
          bundle_id: activeBundle?.id,
          origin: origin,
          invoice_id: data.invoice_id,
          type: selectedCard ? 'saved_card' : 'new_card',
          partner_type: isIuguActive ? 'iugu' : 'pagar_me',
          partner_type_saved_card: selectedCard
            ? selectedCard?.partner_type
            : '',
          profileId: userInfo?.profile?.id,
        });
      }
      customTracking({
        schema:
          'iglu:br.com.getninjas/credit_card_finish_payment_click/jsonschema/1-0-0',
        data: {
          profile_id: checkoutData.profile.id,
          bundle_id: activeBundle.id,
          action: 'click',
          invoice_id: data.invoice_id,
          step: 'credit_card_payment',
          label: 'click_purchase_success',
        },
      });
      if (!appButton) {
        sendMessage();
      }

      count = 0;
    } catch (error) {
      setIsLoading(false);

      if (count > 0 || !!selectedCard) {
        showToast({
          message:
            error.response?.data?.clientMessage || 'Erro ao relizar pagamento',
          type: 'error',
        });
      }

      if (isMercadoPago && !selectedCard && count === 0) {
        count++;
        handleErrorsMercadoPago(error, requestInfoData);
        await handleBuyWithCreditCard();

        return;
      }

      if (window?.newrelic) {
        const errorRequestData = parseErrorMessage(error);

        window.newrelic.noticeError(error, {
          step: 'creditcard_payment_error',
          bundle_id: activeBundle?.id,
          status: error?.response?.status,
          origin: origin,
          message: error?.response?.data?.clientMessage || error?.message,
          type: selectedCard ? 'saved_card' : 'new_card',
          partner_type: isIuguActive ? 'iugu' : 'pagar_me',
          partner_type_saved_card: selectedCard
            ? selectedCard?.partner_type
            : '',
          request_info: !!selectedCard
            ? JSON.stringify(requestInfoData)
            : errorRequestData,
          profileId: userInfo?.profile?.id,
        });

        window.newrelic.addPageAction('webcheckout_action', {
          step: 'creditcard_payment_error',
          bundle_id: activeBundle?.id,
          status: error?.response?.status,
          origin: origin,
          message: error?.response?.data?.clientMessage || error?.message,
          type: selectedCard ? 'saved_card' : 'new_card',
          partner_type: isIuguActive ? 'iugu' : 'pagar_me',
          partner_type_saved_card: selectedCard
            ? selectedCard?.partner_type
            : '',
          request_info: !!selectedCard
            ? JSON.stringify(requestInfoData)
            : errorRequestData,
          profileId: userInfo?.profile?.id,
        });
      }
    }
  }

  const sendMessage = () => {
    if (
      (window as any).webkit &&
      (window as any).webkit.messageHandlers &&
      (window as any).webkit.messageHandlers.checkoutFinalized
    ) {
      (window as any).webkit.messageHandlers.checkoutFinalized.postMessage(
        'checkoutFinalized'
      );
    }
  };

  function handleShowRecurrencyModal(value: boolean) {
    updateCheckoutData((oldState) => ({
      ...oldState,
      showRecurrencyModal: value,
    }));
  }

  function handleChangeRecurrencyCheckbox() {
    if (creditCardData.renew) {
      handleShowRecurrencyModal(true);
    }

    setCreditCardData((oldState) => ({
      ...oldState,
      renew: !creditCardData.renew,
    }));

    window.tagManager.eventCategory('invoice', {
      eventAction: 'auto-renew',
      eventLabel: true,
    });
  }

  function handleTurnOffAutoBuy() {
    setCreditCardData((oldState) => ({
      ...oldState,
      renew: false,
    }));

    handleShowRecurrencyModal(false);
  }

  function handleKeepRecurrency() {
    setCreditCardData((oldState) => ({
      ...oldState,
      renew: true,
    }));

    handleShowRecurrencyModal(false);
  }

  function checkIsDisabledButton() {
    if (selectedCard && !isLoading) {
      if (isMercadoPago) {
        const fieldsWithError = Object.keys(fieldErrorMessage).filter(
          (field) => field
        );

        const emptyFields = !creditCardData.cvv;

        const hasInvalidValue = fieldsWithError.length > 0;
        return hasInvalidValue || isLoading || emptyFields;
      }

      return false;
    }

    const fieldsWithError = Object.keys(fieldErrorMessage).filter(
      (field) => field
    );

    const emptyFields =
      !creditCardData.name ||
      !creditCardData.cvv ||
      !creditCardData.number ||
      !creditCardData.validDate;

    const hasInvalidValue = fieldsWithError.length > 0;
    return hasInvalidValue || isLoading || emptyFields;
  }

  function validateFields(value, field) {
    if (!value) {
      return 'Campo obrigatório';
    }

    if (
      creditCardPatterns[field] &&
      !new RegExp(creditCardPatterns[field].regexp).test(value)
    ) {
      return creditCardPatterns[field].error;
    }
  }

  function handleTriggerEventRedirectToApp() {
    customTracking({
      schema: '',
      data: {
        profile_id: checkoutData.profile.id,
        bundle_id: activeBundle.id,
        action: 'click',
        step: 'credit_card_payment',
        label: 'go_to_app',
      },
    });
  }

  async function handleApplyDiscounts() {
    try {
      const { data } = await axiosInstance({
        method: 'POST',
        url: `${process.env.NEXT_PUBLIC_API_PRO}/purchase/discounts`,
        headers: {
          'Content-Type': 'application/json',
        },
        data: {
          authToken: checkoutData.authToken,
          bundle: activeBundle.purchaseLink,
          coupon: checkoutData.coupon,
          origin,
          user_state: userState,
        },
      });

      const bundleWithDiscounts = {
        ...activeBundle,
        totalPrice: data.current_value,
        installmentValue: data.current_installments_details.max_part_price,
        installmentsLabel: data.current_value_installments,
        installments: data.installments,
      };

      updateCheckoutData((oldState) => {
        const bundles = oldState.bundlesData.filter(
          (bundle) => bundle.nameId !== checkoutData.currentBundleName
        );

        return {
          ...oldState,
          bundlesData: [...bundles, bundleWithDiscounts].sort(
            (a, b) => a.id - b.id
          ),
        };
      });
      showToast({
        type: 'success',
        message: 'Cupom aplicado',
      });
    } catch (error) {
      showToast({
        type: 'error',
        message:
          error.response.data.clientMessage ||
          'Não foi possivel aplicar o cupom',
      });
    }
  }

  useEffect(() => {
    customTracking({
      schema: 'iglu:br.com.getninjas/payment_tab_click/jsonschema/1-0-0',
      data: {
        payment_type: 'new_credit_card',
        profile_id: checkoutData.profile.id,
        bundle_id: activeBundle.id,
      },
    });
  }, []);

  const isCardSelected = (card: IPaymentCard) => {
    return selectedCard?.display_number === card.display_number;
  };

  const handleSetSelectedCard = (card: IPaymentCard) => {
    if (card === selectedCard) {
      handleChangeValueForm('', 'cvv');
      setFieldErrorMessage({});
      setSelectedCard(null);
      return;
    }

    handleChangeValueForm('', 'cvv');
    setFieldErrorMessage({});
    setSelectedCard(card);
  };

  const cardBrand = (brand: string) => {
    if (brand === 'master') return 'mastercard';

    return brand.toLowerCase();
  };

  const GreenCheckIcon = () => (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
        fill="#00C853"
      />
    </svg>
  );

  const additionalInfo = () => {
    return (
      <div className={styles.wrapper}>
        {selectedCard && isMercadoPago && (
          <div className={`credit-card-token ${styles['credit-card-token']}`}>
            <TextField
              id="credit-cvv"
              label="CVV"
              placeholder="Cod. de Segurança"
              value={creditCardData.cvv}
              onChange={(evt) => handleChangeValueForm(evt.target.value, 'cvv')}
              errorMessage={fieldErrorMessage.cvv}
            />
          </div>
        )}

        <select
          className={`credit-installments ${styles['credit-installments']}`}
          onChange={(evt) =>
            handleChangeValueForm(evt.target.value, 'numberInstallments')
          }
        >
          {activeBundle.installments.map(({ label, value }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>

        <div className={`credit-renew ${styles['credit-renew']}`}>
          <input
            type="checkbox"
            id="renew"
            checked={creditCardData.renew}
            onChange={handleChangeRecurrencyCheckbox}
          />
          <label htmlFor="renew">Renovação automatica</label>
        </div>

        <div className={`credit-coupon ${styles['credit-coupon']}`}>
          <input
            type="checkbox"
            id="coupon"
            onChange={() => setShowCoupon(!showCoupon)}
          />
          <label htmlFor="coupon">Usar cupom</label>
        </div>

        {showCoupon && (
          <div className={`coupon__container ${styles['coupon__container']}`}>
            <TextField
              id="credit-coupon"
              className={`coupon__input ${styles['coupon__input']}`}
              label="Cupom"
              placeholder="Cupom"
              value={checkoutData.coupon}
              onChange={(evt) =>
                updateCheckoutData((oldState) => ({
                  ...oldState,
                  coupon: evt.target.value,
                }))
              }
            />

            <Button.Root
              size="small"
              variation="secondary"
              onClick={handleApplyDiscounts}
            >
              Aplicar
            </Button.Root>
          </div>
        )}
      </div>
    );
  };

  return showCompletedPurchase ? (
    <div className={`completed-purchase ${styles['completed_purchase']}`}>
      <img
        width="140px"
        height="140px"
        src={`${process.env.NEXT_PUBLIC_ASSETS_PREFIX}/images/completed-purchase.svg`}
        alt=""
      />
      <Typography.Subtitle size="l">
        Pagamento realizado com sucesso!
      </Typography.Subtitle>
      {appButton ? (
        <>
          <Typography.Text className={styles.text}>
            Entre no aplicativo GetNinjas para encontrar os melhores serviços e
            aproveitar suas moedas.
          </Typography.Text>
          <Button.Link
            href="https://www.getninjas.com.br/download/app"
            fullWidth
            size="small"
            variation="primary"
            onClick={handleTriggerEventRedirectToApp}
          >
            Ir para o aplicativo
          </Button.Link>
        </>
      ) : (
        <Typography.Text className={styles.text}>
          Encontre os melhores serviços e aproveite suas moedas.
        </Typography.Text>
      )}
    </div>
  ) : (
    <div className={`credit-view-wrapper ${styles['credit-view-wrapper']}`}>
      {paymentCards?.length > 0 && (
        <>
          <Typography.Subtitle size="m" className={styles.title}>
            Meus cartões
          </Typography.Subtitle>
          <div className={styles['saved-cards-wrapper']}>
            <div className={styles.divider} />
            {paymentCards.map((card: IPaymentCard) => (
              <div
                key={card.display_number}
                className={`${styles.savedCard} ${!!isCardSelected(card) ? styles['savedCard--selected'] : ''}`}
                onClick={() => handleSetSelectedCard(card)}
              >
                <div className={styles.savedCard__logo}>
                  <img
                    src={`https://raw.githubusercontent.com/aaronfagan/svg-credit-card-payment-icons/main/flat/${cardBrand(card.brand?.toLowerCase())}.svg`}
                    alt={`Logo ${card.brand}`}
                  />
                </div>
                <div className={styles.savedCard__details}>
                  <p className={styles.savedCard__number}>
                    Cartão de Crédito {card.display_number}
                  </p>
                  <div className={styles.savedCard__benefits}>
                    <div className={styles.savedCard__benefitItem}>
                      <GreenCheckIcon />
                      <span>Parcele em até 3x</span>
                    </div>
                    <div className={styles.savedCard__benefitItem}>
                      <GreenCheckIcon />
                      <span>{card.disclaimer || 'Receba moedas na hora!'}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className={`credit-view ${styles['credit-view']}`}>
            {selectedCard && additionalInfo()}
          </div>
        </>
      )}
      <div className={`credit-view ${styles['credit-view']}`}>
        {!selectedCard && (
          <>
            <TextField
              id="credit-card-number"
              label="Número do cartão"
              placeholder="0000 0000 0000 0000"
              value={creditCardData.number}
              onChange={(evt) =>
                handleChangeValueForm(evt.target.value, 'number')
              }
              errorMessage={fieldErrorMessage.number}
              disabled={!!selectedCard}
            />
            <TextField
              id="credit-card-name"
              label="Nome do cartão"
              placeholder="Nome como escrito no cartão"
              value={creditCardData.name}
              onChange={(evt) =>
                handleChangeValueForm(evt.target.value, 'name')
              }
              errorMessage={fieldErrorMessage.name}
              disabled={!!selectedCard}
            />
            <div
              className={`credit-security-info ${styles['credit-security-info']}`}
            >
              <TextField
                id="credit-date"
                label="Válido até"
                placeholder="MM/AA"
                value={creditCardData.validDate}
                onChange={(evt) =>
                  handleChangeValueForm(evt.target.value, 'validDate')
                }
                errorMessage={fieldErrorMessage.date}
                disabled={!!selectedCard}
              />
              <TextField
                id="credit-cvv"
                label="CVV"
                placeholder="Cod. de Segurança"
                value={creditCardData.cvv}
                onChange={(evt) =>
                  handleChangeValueForm(evt.target.value, 'cvv')
                }
                errorMessage={fieldErrorMessage.cvv}
                disabled={!!selectedCard}
              />
            </div>
          </>
        )}

        {!selectedCard && additionalInfo()}

        <Button.Root
          type="button"
          fullWidth
          size="small"
          onClick={handleBuyWithCreditCard}
          isLoading={isLoading}
          disabled={checkIsDisabledButton()}
        >
          Concluir pagamento
        </Button.Root>

        <RecurrencyModal
          showRecurrencyModal={checkoutData.showRecurrencyModal}
          setShowRecurrencyModal={handleShowRecurrencyModal}
          setKeepRecurrency={handleKeepRecurrency}
          setTurnOffAutoBuy={handleTurnOffAutoBuy}
        />
      </div>
    </div>
  );
};
